import React, { FC } from 'react'
import Link from 'next/link'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { Icon } from '@/client/components'
import { IconDot } from '@/client/components/CustomerService/styles'
import { CustomerServiceProps } from '@/client/components/CustomerService/types'
import font from '@/client/components/theme/font'
import { useConfig } from '@/client/context/Config/ConfigProvider'
import { useI18n } from '@/locales'

// REL-888: IANA timezone that the "uk" locale should always be displayed in, regardless of the visitor's browser/OS timezone.
const UK_TIME_ZONE = 'Europe/London'

/**
 * Shows the availability of the customer service.
 * The locale resp. language changes the way the timezone (e.g. CET/MEZ) is written as well as the time format (AM/PM, 24h)
 * The timezone shown with the time is related to the user browser locale settings, as we do not force a specific
 * timezone on the DateTimeFormat formatter.
 * REL-888 Exception: for the "uk" locale the time is always shown in UK time (Europe/London), no matter which timezone
 * the visitor's browser resides in, together with the UK timezone abbreviation (GMT in winter, BST in summer).
 *
 * @param domain Needed to get data from API
 * @param showOpeningTime true if we want to show "Availabale from/to .." otherwise we show icon with phone and mail
 * @param supportPhone Support phone number (Can be removed in case we have it from API)
 * @param children
 *
 * @constructor
 */
const CustomerService: FC<CustomerServiceProps> = ({ showOpeningTime, supportPhone, children }) => {
  // Take the language/local from the next router (click on the language icon)
  const { locale } = useRouter()
  const { shopConfig } = useConfig()

  // Alternatively, load language from the browser.
  // userLocale locale from browser
  // locale is the local selected in the shop
  // en-GB|it|es|nl 17:00 CEST
  // en|en-US       5:00 PM GMT+2
  // fr|fr-FR       17:00 UTC+2
  // de             17:00 MESZ
  // en-AU          5:00 pm GMT+2
  // uk             5 pm GMT, 4:30 pm BST (always Europe/London, regardless of visitor timezone)

  // We do this as we have no proper locale indicating language and country
  const userLocale = () => {
    if (shopConfig?.domain.endsWith('.au')) {
      return 'en-AU'
    }
    if (shopConfig?.domain.endsWith('.com')) {
      return 'en-US'
    }
    if (locale == 'en') {
      return 'en-GB'
    }
    return locale
  } //typeof navigator !== 'undefined' ? navigator.language : locale

  // REL-888: The "uk" locale must always display times in UK time, independent of the visitor's own timezone.
  const isUkLocale = locale === 'uk'

  // Formatter should use out-of-the-box local region settings.
  // For the "uk" locale we pin the timezone to Europe/London, so the abbreviation is always GMT or BST.
  // "uk" is Ukrainian for Intl (24h), so we use en-GB to get the 12h format (e.g. 4:30 pm GMT).
  const formatterHourAndTime = new Intl.DateTimeFormat(isUkLocale ? 'en-GB' : userLocale(), {
    hour: 'numeric',
    minute: 'numeric',
    ...(isUkLocale ? { timeZone: UK_TIME_ZONE, hour12: true, timeZoneName: 'short' } : { timeZoneName: 'short' })
  })

  // We use the formatter to get the weekday translated
  const formatterWeekdayShort = new Intl.DateTimeFormat(isUkLocale ? 'en-GB' : userLocale(), {
    hour: 'numeric',
    minute: 'numeric',
    weekday: 'short',
    ...(isUkLocale ? { timeZone: UK_TIME_ZONE, hour12: true, timeZoneName: 'short' } : { timeZoneName: 'short' })
  })

  // For the "uk" locale full hours are shown without minutes, e.g. "4 pm" instead of "4:00 pm".
  const formatTime = (formatter: Intl.DateTimeFormat, date: Date) =>
    isUkLocale ? formatter.format(date).replace(/:00(?=\s)/, '') : formatter.format(date)

  const supportEmail = shopConfig?.infoEmail

  // When we cannot get the available time, we show green
  if (!shopConfig?.openingTimeFrom || !shopConfig.openingTimeFrom) {
    return StatusMessage({
      supportIsAvailable: undefined,
      formattedTimeFrom: '',
      formattedTimeTo: '',
      showOpeningTime,
      supportPhone,
      supportEmail,
      children
    })
  }

  // In case of weekends we get the next working day right from the API
  // In this case and in case of public holidays (not taken into account from API yet), we should indicate that
  // the opening hour is related to a different day and not today
  const dateFrom = new Date(shopConfig.openingTimeFrom)
  // the nextWorkingDay set indicates that we have a public holiday today and returns the next working day
  if (shopConfig.nextWorkingDay) {
    dateFrom.setDate(shopConfig.nextWorkingDay.getDate())
    dateFrom.setMonth(shopConfig.nextWorkingDay.getMonth())
    dateFrom.setFullYear(shopConfig.nextWorkingDay.getFullYear())
  }

  const isToday = (date: Date) => date.toDateString() === new Date().toDateString()

  const formattedTimeFrom = isToday(dateFrom)
    ? formatTime(formatterHourAndTime, dateFrom)
    : formatTime(formatterWeekdayShort, dateFrom)

  const dateTo = new Date(shopConfig.openingTimeTo)
  const formattedTimeTo = formatTime(formatterHourAndTime, dateTo)

  const supportIsAvailable = isCurrentDateInRange(dateFrom, dateTo)

  return StatusMessage({
    supportIsAvailable,
    formattedTimeFrom,
    formattedTimeTo,
    showOpeningTime,
    supportPhone,
    supportEmail,
    children
  })
}

/**
 * Build the styled message with a nice status icon
 *
 * @param supportIsAvailable
 * @param formattedTimeFrom
 * @param formattedTimeTo
 * @param showOpeningTime
 * @param supportPhone
 * @param supportEmail
 * @param children
 * @constructor
 */
const StatusMessage = ({
  supportIsAvailable,
  formattedTimeFrom,
  formattedTimeTo,
  showOpeningTime,
  supportPhone,
  supportEmail,
  children
}) => {
  const t = useI18n()

  // Spagettification ;-)
  const message = showOpeningTime ? (
    supportIsAvailable != undefined && supportIsAvailable ? (
      <Link style={{ textDecoration: 'none' }} href={`tel:${supportPhone}`} passHref>
        {t('CUSTOMER_SERVICE_AVAILABLE_UNTIL')} {formattedTimeTo}
      </Link>
    ) : supportIsAvailable != undefined && !supportIsAvailable ? (
      `${t('CUSTOMER_SERVICE_AVAILABLE_FROM')} ${formattedTimeFrom}`
    ) : (
      <></>
    )
  ) : supportIsAvailable ? (
    <>
      {t('CUSTOMER_SERVICE_TITLE')}:&nbsp;
      <NextLink href={`tel:${supportPhone}`} passHref legacyBehavior>
        <span style={{ fontWeight: font.weight.bold }}>{supportPhone}</span>
      </NextLink>
      <NextLink href={`mailto:${supportEmail}`} passHref legacyBehavior>
        <Icon name='email-relaunch' size='s5' className='icon' />
      </NextLink>
    </>
  ) : (
    <>
      {t('CUSTOMER_SERVICE_TITLE')}:&nbsp;
      <span style={{ fontWeight: font.weight.bold }}>{supportPhone}</span>
      <NextLink href={`mailto:${supportEmail}`} passHref legacyBehavior>
        <Icon name='email-relaunch' size='s5' className='icon' />
      </NextLink>
    </>
  )

  const icon = supportIsAvailable != undefined ? <IconDot isOk={supportIsAvailable}></IconDot> : <></>
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {icon}
      {message}
      {supportIsAvailable} {children}
    </div>
  )
}

function isCurrentDateInRange(startDate: Date, endDate: Date): boolean {
  const now = new Date()
  return now >= startDate && now <= endDate
}

export default CustomerService
